import { STATUSES, validStatuses } from '#server/middleware/status-validators';
import _noop from 'lodash/noop';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Dialog, DialogChainObject } from 'quasar';
import { ref } from 'vue';
import { useAccountStore } from '#src/store/account';
import { useDeviceStore } from '#src/store/device';
import { api } from 'services/api';
import useLogger from 'composables/logger';
import useSetTimeout from 'composables/set-timeout';
import useSkus, { SKU_TYPE } from 'composables/skus';
import Account from '@models/Account';
import Device from '@models/Device';
import Order from '@models/Order';
import ProgressDialog from 'components/ProgressDialog.vue';
import SimpleDialog from 'components/SimpleDialog.vue';
import { Router } from 'vue-router';
import { updateTitle } from 'services/title';
import { useDayjs } from 'composables/dayjs';
import { useDevicesByOrder } from 'composables/device';
import useWebsocket from 'composables/websocket';
import { usePartner } from 'composables/partner';

export const useOrderStore = defineStore({
  id: 'order',
  state: () => ({
    accountInfo: {} as { [orderNumber: string]: OrderAccountInfo },
    accountTags: {} as { [tagId: number]: ShipStationTag },
    claimProgress: {} as { [orderNumber: string]: { finished: number; total: number } },
    currentOrderNumber: null as string | null,
    failedToSync: false,
    gettingOrderByTracking: false,
    gettingTags: false,
    isInitializing: false,
    orderNotesInProgress: {} as { [orderNumber: string]: boolean },
    orderNotFound: false,
    orderRequests: {} as { [orderNumber: string]: Promise<void> },
    orders: {} as { [orderNumber: string]: Order },
    ordersInProgress: {} as { [orderNumber: string]: boolean },
    ordersUpdating: {} as { [trackingNumber: string]: boolean },
    progressDialog: null as DialogChainObject | null,
    progressDialogType: 'claim' as 'claim' | 'replacement',
    shippingInProgress: {} as { [orderNumber: string]: boolean },
    trackingNumbers: {} as { [orderNumber: string]: string },
  }),
  getters: {
    currentOrder(state): Order | null {
      const orderNumber = <string>state?.currentOrderNumber;
      const order = state.orders[orderNumber];

      if (!order) {
        return null;
      }

      const accountInfo = this.accountInfo[orderNumber];

      return {
        ...order,
        accountFromNotes: accountInfo?.idFromNotes,
        accountOrigin: accountInfo?.origin || 'order',
        accountConfirmed: accountInfo?.userConfirmed || false,
      };
    },
    currentOrderAccount(): Account | null {
      const { accounts } = storeToRefs(useAccountStore());

      const accountKey = this.currentOrderAccountKey;
      if (!accountKey) {
        return null;
      }

      return accounts.value[accountKey] || null;
    },
    currentOrderAccountKey(): string | null {
      if (!this.currentOrder) {
        return null;
      }

      const { accountKey, accountFromNotes, accountOrigin} = this.currentOrder;

      // Use account from notes if specified
      if (accountOrigin === 'notes' && accountFromNotes) {
        return accountFromNotes;
      }

      // Use account associated with order by default
      return accountKey || null;
    },
    currentOrderDevices(): Device[] {
      const { devicesByOrder } = storeToRefs(useDeviceStore());
      return devicesByOrder.value[<string>this.currentOrder?.orderNumber] || [];
    },
    currentOrderFulfilled(): boolean {
      return this.currentOrder?.totalDevices === this.currentOrderFulfilledDevices.length;
    },
    /**
     * Returns the fulfilled devices associated with the order (exlcuding the ones that were replaced).
     */
    currentOrderFulfilledDevices(): Device[] {
      return this.currentOrderDevices.filter(
        ({ isFulfilled, replacedBy, willReplace }) => isFulfilled && !replacedBy && !willReplace
      );
    },
    /**
     * Returns the non-fulfilled devices associated with the order (exlcuding the ones that are to be replaced).
     */
    currentOrderNonFulfilledDevices(): Device[] {
      return this.currentOrderDevices.filter(
        ({ isFulfilled, replacedBy, willReplace }) => !isFulfilled && !replacedBy && !willReplace
      );
    },
    currentOrderDevicesNeeded(): number {
      return (this.currentOrder?.totalDevices || 0) - this.currentOrderFulfilledDevices.length;
    },
    currentOrderDevicesNeededBySku(): { [sku: string]: number } {
      const devicesNeededBySku: { [sku: string]: number } = {};
      this.currentOrder?.items?.map(({ quantity, sku }) => {
        const skuDevices = this.currentOrderDevices.filter(
          ({ replacedBy, sku: deviceSku, willReplace }) => deviceSku === sku && !replacedBy && !willReplace
        );
        devicesNeededBySku[sku] = quantity - skuDevices.length;
      });
      return devicesNeededBySku;
    },
    currentOrderReady(): boolean {
      return this.currentOrderDevicesNeeded - this.currentOrderDevices.filter(({ isNew }) => isNew).length === 0;
    },
    /**
     * Returns all of the devices to be replaced.
     */
    currentOrderReplacements(): Device[] {
      return this.currentOrderDevices.filter(({ isFulfilled, willReplace }) => !isFulfilled && willReplace);
    },
    currentOrderInProgress: (state) => {
      if (!state.currentOrderNumber) {
        return false;
      }
      return state.ordersInProgress[state.currentOrderNumber] || false;
    },
    currentOrderIsReplacement(): boolean {
      const { isReplacementSku } = useSkus();
      return isReplacementSku(this.currentOrderType);
    },
    currentOrderType(): SKU_TYPE {
      const { getOrderType } = useSkus();
      const items = this.currentOrder?.items || [];
      return getOrderType(items.map(({ sku }) => sku));
    },
    fulfilledTagId(): number {
      return this.tagIdByName['Fulfilled'];
    },
    hasErrors(): boolean {
      return this.currentOrderDevices.some(({ error }) => Boolean(error));
    },
    ordersAwaitingShipment(state): Order[] {
      return Object.values(state.orders).filter(
        (order) =>
          order.orderStatus === 'shipped' && (!order.tagIds || !order.tagIds.includes(this.tagIdByName['Fulfilled']))
      );
    },
    replacedDevices(): Device[] {
      return this.currentOrderDevices.filter(({ replacedBy, willReplace }) => Boolean(replacedBy) && willReplace);
    },
    replacementsNeeded(): number {
      return this.currentOrderDevices.filter(({ replacedBy, willReplace }) => willReplace && !replacedBy).length;
    },
    shippableDevices(): Device[] {
      return this.currentOrderDevices.filter(({ isNew }) => isNew);
    },
    shippedOrders(state): Order[] {
      const shippedOrders = Object.values(state.orders).filter(
        (order) => order.orderStatus === 'shipped' && order.tagIds?.includes(this.tagIdByName['Fulfilled'])
      );

      shippedOrders.sort((a, b) => {
        if (!a.shipDateUnix) {
          return -1;
        } else if (!b.shipDateUnix) {
          return 1;
        } else if (b.shipDateUnix < a.shipDateUnix) {
          return -1;
        } else if (b.shipDateUnix > a.shipDateUnix) {
          return 1;
        }
        return 0;
      });

      return shippedOrders;
    },
    tagIdByName: (state) => {
      const tagIdByName: { [name: string]: number } = {};
      Object.values(state.accountTags).forEach((tag) => {
        tagIdByName[tag.name] = tag.tagId;
      });
      return tagIdByName;
    },
  },
  actions: {
    associate({ orderNumber, serial }: { orderNumber: string; serial: string }) {
      const deviceStore = useDeviceStore();
      const { devicesByOrder } = storeToRefs(deviceStore);

      // Get available device slots
      const skus: { [sku: string]: number } = {};
      this.orders[orderNumber]?.items?.forEach((item) => {
        skus[item.sku] = item.quantity;
      });

      if (_isEmpty(skus)) {
        return {
          error: 'No SKUs were found for this order.',
        };
      }

      const devices = devicesByOrder.value[orderNumber] || [];

      // Remove already filled SKUs
      devices
        .filter(
          ({ isFulfilled, isNew, replacedBy, willReplace }) => (isFulfilled && !replacedBy && !willReplace) || isNew
        )
        .forEach((device) => {
          const sku = <string>device.sku;
          skus[sku] = skus[sku] - 1;
        });

      const device = deviceStore.devices[serial];

      // Find suitable SKU for device
      const { findSkuForDevice } = useSkus();
      const { skusForDevice, deviceType, skuTypes } = findSkuForDevice(Object.keys(skus), device);

      if (!skusForDevice.length) {
        return {
          error: `Device (${deviceType}) does not match any SKU on this order: ${skuTypes.map(({ sku, type}) => `${sku} (${type})`).join(', ')}`,
        };
      }

      const skuForDevice = skusForDevice.find(({sku}) => skus[sku] > 0);
      if (!skuForDevice) {
        return {
          error: `No more devices can be added to the "${skusForDevice.map(({ sku }) => sku).join('"/"')}" SKU${
            skusForDevice.length > 1 ? 's' : ''
          }.`,
        };
      }

      const replace = <Device>this.handleReplacement({ devices, serial, sku: skuForDevice.sku });

      deviceStore.$patch((state) => {
        state.devices[serial] = {
          ...state.devices[serial],
          orderNumber,
          needsData: true,
          serial,
          sku: skuForDevice.sku,
          isNew: true,
          replaces: replace?.serial,
        };

        if (replace?.serial) {
          state.devices[replace.serial] = {
            ...state.devices[replace.serial],
            replacedBy: serial,
          };
        }
      });

      return { success: true };
    },
    async claimDevices(devices: Device[]) {
      const deviceStore = useDeviceStore();
      const order =<Order>this.currentOrder;

      await api.post<{ success: boolean }>('/claim/save-pending', {
        orderNumber: order.orderNumber,
        devices: devices.map(({ serial, sku }) => ({ serial, sku })),
        trackingNumber: order.trackingNumber,
      });

      const account = this.currentOrderAccount;

      if (!account) {
        this.showErrorDialog('Unable to claim devices without an associated account.');
        return devices.map(({ serial }) => ({ serial, success: false }));
      }

      const { partner, getPartner } = usePartner(ref(account.partnerKey));

      if (!partner.value) {
        // Get the partner if it doesn't exist
        await getPartner();
      }

      if (!partner.value) {
        this.showErrorDialog('Unable to claim devices without an associated partner.');
        return devices.map(({ serial }) => ({ serial, success: false }));
      }

      const { id: partnerId } = partner.value;

      this.showProgressDialog();

      let results;
      try {
        const response = await api.post<ClaimResult[]>(
          '/claim',
          {
            accountKey: account.key,
            customerEmail: order.customerEmail,
            devices: devices.map(({ serial, sku }) => ({ serial, sku })),
            items: order.items?.map(({ quantity, sku }) => ({ quantity, sku })),
            orderNumber: order.orderNumber,
            partnerId,
            trackingNumber: order.trackingNumber,
          },
          {
            validateStatus: validStatuses(STATUSES.NOT_FOUND),
          }
        );

        const claimedDevices = response.data.filter(({ success }) => success);
        const devicesWithError = response.data.filter(({ success }) => !success);

        if (claimedDevices.length) {
          deviceStore.markAsClaimed(claimedDevices.map(({ serial }) => serial));
        }

        if (devicesWithError.length) {
          devicesWithError.forEach(({ serial, error }) => {
            deviceStore.setDeviceError({
              error: <string>error,
              serial,
            });
          });
        }

        results = response.data;
      } catch (error) {
        const logger = useLogger();

        if (axios.isAxiosError(error)) {
          logger.error(
            `claim error: ${<string>(<unknown>error.response?.status)} ${<string>error.response?.data}`,
            error
          );
        } else {
          logger.error('claim error', <Error>error);
        }
        results = [{ error }];
      } finally {
        const wait = useSetTimeout();
        await wait(1000);
        this.progressDialog?.hide();
      }

      return results;
    },
    showErrorDialog(message: string) {
      const dialog = Dialog.create({
        component: SimpleDialog,
        componentProps: {
          title: 'Cannot Ship this Order',
          type: 'negative',
          message,
          onOk() {
            dialog.hide();
          },
        },
      });
    },
    setFailedToSync(didFail: boolean) {
      this.failedToSync = didFail;
    },
    showProgressDialog() {
      const isReplacement = this.currentOrderIsReplacement;

      this.progressDialogType = isReplacement ? 'replacement' : 'claim';

      const dialog = Dialog.create({
        component: ProgressDialog,
        componentProps: {
          title: `${isReplacement ? 'Replacing' : 'Claiming'} Devices`,
          message: `Starting ${isReplacement ? 'replacement' : 'claim'} process...`,
          onCancel() {
            Dialog.create({
              component: SimpleDialog,
              componentProps: {
                title: 'Cancel?',
                message: 'Are you sure you want cancel while shipment is being processed?',
                prompt: true,
                dismissText: 'No',
                okColor: 'negative',
                okText: 'Yes',
              },
            }).onOk(() => {
              dialog.hide();
            });
          },
        },
      });

      this.progressDialog = dialog;

      return dialog;
    },
    async getAccountFromNotes(orderNumber: string) {
      const accountInfo = this.$state.accountInfo[orderNumber] || {};

      if (accountInfo.idFromNotes) {
        return accountInfo.idFromNotes;
      }

      this.$state.accountInfo[orderNumber] = {
        ...accountInfo,
        idFromNotes: undefined,
      }
      this.$state.orderNotesInProgress[orderNumber] = true;

      const { data } = await api.get<string[]>(`/orders/${orderNumber}/notes`);

      const notes = data.reverse();

      let accountId = null;
      for (let i = 0; i < notes.length; i += 1) {
        const accountIdMatch = notes[i].match(/[\w\d]{20,}/g);
        if (accountIdMatch !== null) {
          const [id] = accountIdMatch;
          accountId = id;
          break;
        }
      }

      if (accountId) {
        this.$state.accountInfo[orderNumber] = {
          ...accountInfo,
          idFromNotes: accountId,
        }
        const accountStore = useAccountStore();
        await accountStore.getAccount(accountId);
      }

      delete this.$state.orderNotesInProgress[orderNumber];

      return accountId;
    },
    async getAccountTags() {
      const { gettingTags } = this;

      if (gettingTags) {
        return;
      }

      this.gettingTags = true;

      try {
        const { data: tags } = await api.get<ShipStationTag[]>('/orders/account-tags');
        const accountTags: { [tagId: number]: ShipStationTag } = {};
        tags.forEach((tag) => {
          accountTags[tag.tagId] = tag;
        });
        this.accountTags = accountTags;
      } catch (error) {
        const logger = useLogger();
        logger.error('getAccountTags error:', <Error>error);
      }

      this.gettingTags = false;
    },
    async getOrder(orderNumber: string) {
      const accountStore = useAccountStore();
      const deviceStore = useDeviceStore();
      const orderStore = useOrderStore();
      const { accounts } = storeToRefs(accountStore);
      const { getDevices } = useDevicesByOrder(ref(orderNumber));

      const { ordersInProgress, orderNotFound } = this;

      if (orderNotFound) {
        return;
      }

      this.orderNotFound = false;
      this.currentOrderNumber = orderNumber;

      let resolvePromise = _noop;
      this.$state.orderRequests[orderNumber] = new Promise((resolve) => {
        resolvePromise = resolve;
      });

      if (!ordersInProgress[orderNumber]) {
        try {
          this.ordersInProgress[orderNumber] = true;
          const { data } = await api.get<HydratedOrder>(`/orders/${orderNumber}`);

          this.insertOrders({ [data.orderNumber]: data });
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            this.orderNotFound = true;
          } else {
            const logger = useLogger();
            logger.error('getOrder error:', <Error>error);
          }
        }
      }

      if (this.orderNotFound) {
        this.ordersInProgress[orderNumber] = false;
        resolvePromise();
        delete this.$state.orderRequests[orderNumber];
        return;
      }

      const order = this.orders[orderNumber];

      const pendingClaims = await this.getPendingClaims(orderNumber);
      if (pendingClaims.length) {
        await Promise.all(
          pendingClaims.map(async ({ replacingSerial, serial }) => {
            const devices = [];
            if (replacingSerial) {
              devices.push(deviceStore.getDevice(replacingSerial));
            }
            devices.push(deviceStore.getDevice(serial));
            orderStore.associate({ orderNumber, serial });
            await Promise.all(devices);
          })
        );
      }

      this.ordersInProgress[orderNumber] = false;

      const accountKey = order?.accountKey;
      if (accountKey && !accounts.value[accountKey]) {
        void accountStore.getAccount(accountKey);
      }

      void getDevices();

      resolvePromise();
      delete this.$state.orderRequests[orderNumber];
    },
    async getOrderByTrackingNumber(trackingNumber: string) {
      const { gettingOrderByTracking, trackingNumbers } = this;

      let orderNumber = trackingNumbers[trackingNumber] || null;

      if (orderNumber) {
        return orderNumber;
      }

      if (gettingOrderByTracking) {
        return null;
      }

      this.gettingOrderByTracking = true;

      try {
        const { data: order } = await api.get<HydratedOrder>(`/orders?trackingNumber=${trackingNumber}`);
        void this.insertOrders({ [order.orderNumber]: order });
        // TODO: maybe store this in backend memory with order data?
        orderNumber = order.orderNumber;
        this.trackingNumbers[trackingNumber] = orderNumber; // keep track of tracking numbers
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          this.orderNotFound = true;
        } else {
          const logger = useLogger();
          logger.error('getOrderByTrackingNumber error:', <Error>error);
        }
      }

      this.gettingOrderByTracking = false;

      return orderNumber || 'Not Found';
    },
    async getPendingClaims(orderNumber: string) {
      try {
        const { data } = await api.get<PendingClaim[]>(`/orders/${orderNumber}/pending-claims`);
        return data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          const logger = useLogger();
          logger.error('getOrder error:', <Error>error);
        }
        return [];
      }
    },
    getRecentlyShippedOrders() {
      const { ws } = useWebsocket();
      this.isInitializing = true;
      ws.once('order/update', () => {
        this.isInitializing = false;
      });
      ws.send('getOrders');
    },
    handleReplacement({ devices, serial, sku }: { devices: Device[]; serial: string; sku: string }) {
      const deviceStore = useDeviceStore();
      const deviceStoreRefs = storeToRefs(deviceStore);
      const { getSkuType } = useSkus();

      if (![SKU_TYPE.REPLACEMENT, SKU_TYPE.ASSET_REPLACEMENT, SKU_TYPE.DASHCAM_REPLACEMENT].includes(getSkuType(sku))) {
        return;
      }

      // Device may not exist yet
      const device: Device | undefined = deviceStoreRefs.devices.value[serial];

      if (device?.replaces) {
        // Un-replace existing replacement
        const replacedSerial = device.replaces;
        deviceStore.$patch((state) => {
          state.devices[replacedSerial].replacedBy = undefined;
          state.devices[device.serial].replaces = undefined;
        });
      }

      // Find the first device that needs replacement
      return devices.find(({ replacedBy, willReplace }) => willReplace && !replacedBy);
    },
    /**
     * Inserts/merges order data, expecting ShipStationOrder format.
     */
    insertOrders(orderData: OrderDataStore) {
      const convertedOrders = Object.values(orderData).map((order) => this.convertOrder(order));
      const orders = _keyBy(convertedOrders, 'orderNumber');
      this.orders = { ...this.orders, ...orders };
    },
    onRouteChange(router: Router) {
      router.afterEach((to) => {
        const { orderNumber } = to.query;
        if (to.name === 'fulfillment' && orderNumber) {
          this.getOrder(<string>orderNumber).then(() => {
            // Make sure we're still viewing this order
            if (router.currentRoute.value.query.orderNumber !== orderNumber) {
              return
            }
            updateTitle(`Order #${<string>orderNumber} - Fulfillment`);
            if (this.gettingOrderByTracking) {
              this.gettingOrderByTracking = false;
            }
          }).catch((error) => {
            throw error;
          });
        } else {
          this.orderNotFound = false;
          this.currentOrderNumber = <string>orderNumber;
        }
      });
    },
    convertOrder(orderData: HydratedOrder) {
      const { dayjs } = useDayjs();

      // Remove adjustments/discounts that come through as line items
      const lineItems = orderData.items.filter(({ adjustment }) => !adjustment);

      const items = lineItems.map((item) => ({
        orderItemId: item.orderItemId,
        originalSku: item.originalSku,
        sku: item.sku,
        name: item.name,
        quantity: this.getQuantity(item.sku, item.quantity),
      }));

      const shipDate = orderData.shipDate ? dayjs(orderData.shipDate) : null;

      const order = <Order>{
        carrierCode: orderData.carrierCode,
        code: orderData.code,
        customerEmail: orderData.customerEmail,
        modifyDate: dayjs(orderData.modifyDate),
        orderId: orderData.orderId,
        orderNumber: orderData.orderNumber,
        orderStatus: orderData.orderStatus,
        shipDate,
        shipDateUnix: shipDate ? shipDate.unix() : null,
        shipTo: orderData.shipTo,
        sku: orderData.sku,
        remainingDevices: orderData.remainingDevices,
        serviceCode: orderData.serviceCode,
        tagIds: orderData.tagIds,
        totalDevices: items.reduce((total, item) => total + item.quantity, 0),
        items,
      };

      if (orderData.accountKey) {
        order.accountKey = orderData.accountKey;
      }

      if (orderData.accountOwnerKey) {
        order.accountOwnerKey = orderData.accountOwnerKey;
      }

      if (orderData.trackingNumber) {
        order.trackingNumber = orderData.trackingNumber;
      }

      return order;
    },
    getQuantity(sku: string, quantity: number) {
      const { getSkuType } = useSkus();
      return getSkuType(sku) === SKU_TYPE.NON_DEVICE ? 0 : quantity;
    },
    async replaceDevices(devices: Device[]) {
      const deviceStore = useDeviceStore();
      this.showProgressDialog();

      const order = <Order>this.currentOrder;

      let finished = 0;
      this.setClaimProgress({ orderNumber: <string>order.orderNumber, finished, total: devices.length });

      const results = await Promise.all(
        devices.map(async (device) => {
          const { serial } = device;
          let replaceError: string | null = null;
          let success = false;

          try {
            const { data } = await api.post<{ success: boolean; error?: string }>(`/device/${serial}/replace`, {
              accountKey: this.currentOrderAccountKey,
              orderNumber: order.orderNumber,
              replacementSerial: device.replacedBy,
              sku: device.sku,
            });
            success = data.success;
            if (data.error) {
              replaceError = data.error;
            }
          } catch (error) {
            const logger = useLogger();
            if (axios.isAxiosError(error)) {
              logger.axiosError('device replace error', error);
              const data = <{ error: string }>(error.response?.data || {});
              replaceError = data.error;
            } else {
              logger.error('device replace error', <Error>error);
              replaceError = (<Error>error).message;
            }
            success = false;
          }

          if (success) {
            deviceStore.markAsReplaced(device.serial);
          } else {
            deviceStore.setDeviceError({
              serial: device.serial,
              error: <string>replaceError,
              relatedSerial: device.replacedBy,
            });
          }

          finished += 1;
          this.setClaimProgress({ orderNumber: <string>order.orderNumber, finished, total: devices.length });

          return { serial, success, error: replaceError };
        })
      );

      const devicesUpdated = results.filter(({ success }) => success).map(({ serial }) => serial);
      void deviceStore.getDevices({ serials: devicesUpdated, refresh: true });

      const wait = useSetTimeout();
      await wait(1000);
      this.progressDialog?.hide();

      return results;
    },
    setClaimProgress({
      orderNumber,
      finished,
      hasErrors,
      hasWarnings,
      total,
    }: {
      orderNumber: string;
      finished: number;
      hasErrors?: boolean;
      hasWarnings?: boolean;
      total: number;
    }) {
      this.claimProgress = {
        ...this.claimProgress,
        [orderNumber]: {
          finished,
          total,
        },
      };

      const progressUpdate = <{ message: string; progress: number; type: string }>{
        message: `${finished}/${total} ${this.progressDialogType === 'replacement' ? 'replaced' : 'claimed'}`,
        progress: finished / total,
      };

      if (hasErrors) {
        progressUpdate.type = 'negative';
      } else if (hasWarnings) {
        progressUpdate.type = 'warning';
      }

      this.progressDialog?.update(progressUpdate);
    },
    async shipOrder() {
      const { isReplacementSku } = useSkus();
      const orderNumber = <string>this.currentOrderNumber;
      const orderType = this.currentOrderType;

      const { shippingInProgress } = this;
      if (shippingInProgress[orderNumber]) {
        return;
      }

      this.shippingInProgress[orderNumber] = true;

      let result: Device[] | ClaimResult[];
      if (isReplacementSku(orderType)) {
        result = <Device[] & { error: string }[]>await this.replaceDevices(this.replacedDevices);
      } else {
        result = <ClaimResult[]>await this.claimDevices(this.shippableDevices);
      }

      if ((<ClaimResult[]>result).every(({ success }) => success)) {
        void this.markAsShipped(orderNumber);
      }

      delete this.shippingInProgress[orderNumber];

      return result;
    },
    async markAsShipped(orderNumber: string) {
      const { fulfilledTagId, orders, ordersUpdating } = this;

      if (ordersUpdating[orderNumber]) {
        return;
      }

      this.ordersUpdating[orderNumber] = true;

      const order = orders[orderNumber];
      const { orderId } = order;

      let success = false;
      try {
        const response = await api.post<ShipStationSuccessMessage>(`/orders/${orderNumber}/add-tag`, {
          orderId,
          tagId: fulfilledTagId,
        });
        success = response.data.success;
        if (success) {
          this.orders = {
            ...this.orders,
            [<string>order.orderNumber]: { ...order, tagIds: [...(order.tagIds || []), fulfilledTagId] },
          };
        }
      } catch (error) {
        const logger = useLogger();
        logger.error('markAsShipped error:', <Error>error);
      }

      delete this.ordersUpdating[orderNumber];

      return success;
    },
    /**
     * Updates the order (using given order number), expecting Order format.
     */
    updateOrder({ orderNumber, orderData }: { orderNumber: string; orderData: Order }) {
      const order = this.orders[orderNumber];
      this.orders = { ...this.orders, [orderNumber]: { ...order, ...orderData } };
    },
    setOrderAccountOrigin(orderNumber: string, origin: 'notes' | 'order', userConfirmed = false) {
      const accountInfo = this.$state.accountInfo[orderNumber];
      this.$state.accountInfo[orderNumber] = {
        ...accountInfo,
        origin,
        userConfirmed,
      }
    },
  },
});
